const plans = [
  {
    name: 'SIGNATURE ESSENTIAL',
    price: '$297',
    period: 'Quarterly',
    billing: 'Billed every 3 months',
    savings: null,
    desc: 'For proactive patients who value preparedness and consistent care.',
    icon: 'ri-seedling-line',
    highlight: false,
  },
  {
    name: 'SIGNATURE BALANCE',
    price: '$549',
    period: 'Semi-Annual',
    billing: 'Billed every 6 months',
    savings: 'Save $45 vs. quarterly',
    desc: 'For patients actively optimizing health and performance with consistent engagement.',
    icon: 'ri-scales-3-line',
    highlight: true,
  },
  {
    name: 'SIGNATURE ELITE',
    price: '$999',
    period: 'Annual',
    billing: 'Billed once per year',
    savings: 'Save $189 vs. quarterly',
    desc: 'Our highest level of access, value, and continuity for the committed patient.',
    icon: 'ri-vip-crown-line',
    highlight: false,
  },
];

const allFeatures = [
  'Named provider assignment',
  'Direct clinical access via secure text',
  'Unlimited medically necessary telehealth',
  'Monthly in-clinic visit allocation',
  'Two comprehensive lab panels annually',
  'After-hours clinical guidance',
  'Priority prescriptions & care coordination',
];

export default function PlansAndPricing() {
  return (
    <section id="plans" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-16">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-teal-600 mb-4">
            Signature Access
          </span>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-bold text-[#0F1C2E] mb-5">
            Choose Your Plan
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Designed for patients who expect consistency, accountability, and medical excellence — starting at just $99/mo.
          </p>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                plan.highlight
                  ? 'bg-[#0A2818] text-white ring-2 ring-teal-500'
                  : 'bg-[#F8FAFB] border border-gray-100 hover:border-teal-200'
              }`}
            >
              {plan.highlight && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 bg-teal-500 text-white text-xs font-bold rounded-full whitespace-nowrap uppercase tracking-wider">
                    Best Value
                  </span>
                </div>
              )}

              <div className="p-8 flex-1">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-6 ${
                  plan.highlight ? 'bg-teal-500/20' : 'bg-teal-50'
                }`}>
                  <i className={`${plan.icon} text-xl ${plan.highlight ? 'text-teal-400' : 'text-teal-600'}`}></i>
                </div>

                <h3 className={`text-xs font-bold tracking-widest uppercase mb-4 ${
                  plan.highlight ? 'text-teal-400' : 'text-teal-600'
                }`}>
                  {plan.name}
                </h3>

                <div className="mb-2">
                  <span className={`font-serif-display text-5xl font-bold ${plan.highlight ? 'text-white' : 'text-[#0F1C2E]'}`}>
                    {plan.price}
                  </span>
                  <span className={`text-sm ml-2 ${plan.highlight ? 'text-white/60' : 'text-gray-400'}`}>
                    / {plan.period}
                  </span>
                </div>

                <p className={`text-xs mb-2 ${plan.highlight ? 'text-white/50' : 'text-gray-400'}`}>
                  {plan.billing}
                </p>

                {plan.savings && (
                  <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-md mb-4 ${
                    plan.highlight ? 'bg-teal-500/20 text-teal-400' : 'bg-teal-50 text-teal-700'
                  }`}>
                    {plan.savings}
                  </span>
                )}

                {!plan.savings && <div className="mb-4 h-7"></div>}

                <p className={`text-sm leading-relaxed mb-8 ${plan.highlight ? 'text-white/70' : 'text-gray-500'}`}>
                  {plan.desc}
                </p>

                <a
                  href="#contact"
                  className={`block w-full text-center py-3 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    plan.highlight
                      ? 'bg-teal-500 hover:bg-teal-400 text-white'
                      : 'bg-[#0A2818] hover:bg-teal-700 text-white'
                  }`}
                >
                  Get Started
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Included features */}
        <div className="bg-[#F8FAFB] border border-gray-100 rounded-2xl p-8 lg:p-10">
          <div className="text-center mb-8">
            <p className="text-sm font-semibold text-gray-400 uppercase tracking-widest">All plans include</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {allFeatures.map((f) => (
              <div key={f} className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-teal-600 text-base font-bold"></i>
                </div>
                <span className="text-sm text-gray-700 leading-snug">{f}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <a href="#benefits" className="inline-flex items-center gap-2 text-teal-600 text-sm font-semibold hover:text-teal-700 transition-colors cursor-pointer">
              View all benefits
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
